import { Component, OnInit, ChangeDetectorRef, ViewChild, HostListener, Inject, LOCALE_ID } from '@angular/core';
import { TaskService } from 'src/services/task/task.service';
import { PBIService } from 'src/services/pbi/pbi.service';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColumnConfig } from '../filterable-paginated-table/filterable-paginated-table.component';
import { Task } from 'src/classes/task';
import { kebabCaseToTitleCase } from 'src/utils/toTitleCase';
import { interpolate } from 'src/utils/interpolate';

interface TaskEntry {
  id: number;
  name: string;
  type: string;
  workspace: {id: string, name: string};
  report: {id: string, name: string};
  pages: string[];
  pageString: string;
  nextExecution: string;
  active: boolean;
  actions: {class: string, icon?: string, label?: string}[]
}

@Component({
  selector: 'app-task-list-page',
  templateUrl: './task-list-page.component.html',
  styleUrls: ['./task-list-page.component.scss']
})
export class TaskListPageComponent implements OnInit {

  private loadingTaskSession: Promise<any>;
  private loadingWorkspaceSession: Promise<any>;

  public taskList: TaskEntry[];
  public loading = false;
  public pageSize = 10;

  public currentTask: TaskEntry = null;
  @ViewChild('removeModal', { static: true }) removeModal;
  @ViewChild('cloneModal', { static: true }) cloneModal;
  @ViewChild('changeStatusModal', { static: true }) changeStatusModal;

  public error = {
    active: false,
    title: '',
    message: '',
    stack: ''
  };

  public shouldResetCache = false;

  public columns: ColumnConfig[] = [
    {label: 'Index', property: ['index'], hidden: true},
    {label: 'Name', property: ['name'], filter: true, width: "19%"},
    {label: 'Workspace', property: ['workspace', 'name'], filter: true, isLoading: true, hidden: true, width: "18%"},
    {label: 'Report', property: ['report', 'name'], filter: true, width: "18%"},
    {label: 'Type', property: ['type'], filter: true, width: "11%"},
    {label: 'Pages', property: ['pageString'], width: "8%"},
    {label: 'Next Run', property: ['nextExecution'], filter: true, width: "12%"},
    {label: 'Actions', property: ['actions'], width: "210px"}
  ];

  constructor(
    private taskService: TaskService,
    private pbiservice: PBIService,
    private router: Router,
    private modalService: NgbModal,
    private ref: ChangeDetectorRef,
    @Inject(LOCALE_ID) public locale: string
  ) { }

  ngOnInit() {
    this.loading = true;
    this.loadTaskAndLink().then(() => {
      this.loading = false;
    });
    this.onResize(null);
    this.applyLocaleToHeader();
  }

  private applyLocaleToHeader() {
    if (typeof this.locale === "string" && this.locale.toLowerCase().substring(0, 2) === "pt") {
      const portugueseList = ["Index", "Nome", "Workspace", "Relatório", "Tipo", "Páginas", "Próxima Execução", "Ações"];

      portugueseList.forEach((portugueseLabel, index) => this.columns[index].label = portugueseLabel);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.pageSize = Math.floor(interpolate(450, 5, 800, 12, window.innerHeight));
  }

  private async loadTaskAndLink() {
    try {
      const response = await this.taskService.getTaskList(this.shouldResetCache);
      this.shouldResetCache = true;
      this.onTaskAndLinkLoad(response.list);
      this.loading = false;
    } catch(err) {
      if (this.error.active) {
        return;
      }
      this.error.title = 'Error while loading tasks and workspaces';
      this.error.message = err.message;
      this.error.stack = err.stack;
      this.error.active = true;
    }
  }

  onTaskAndLinkLoad(tasks: Task[]) {
    this.loadingTaskSession = undefined;

    this.taskList = tasks.map(task => {
      const isActive = task.status.toLowerCase() === 'active';
      return {
        id: task.id,
        name: task.name,
        type: kebabCaseToTitleCase(task.typeString) || "unknown",
        workspace: {
          id: task.PBIWorkspaceId,
          name: task.PBIWorkspaceName
        },
        report: {
          id: task.PBIReportId,
          name: task.PBIReportName
        },
        pages: task.pages,
        pageString: task.pages.length + " pages",
        nextExecution: isActive ? (task.nextExecution ? (new Date(task.nextExecution)).toLocaleString() : "") : " N/A ",
        active: isActive,
        actions: [
          {
            class: "btn-info btn-status" + (isActive ? "" : " deactivated"),
            icon: isActive ? "fa fa-check-circle" : "fa fa-times-circle",
            label: (isActive ? "Enabled" : "Disabled")
          },
          {class: "btn-info btn-view", icon: "fa fa-eye"},
          {class: "btn-primary btn-edit", icon: "fa fa-pencil"},
          {class: "btn-info btn-view", icon: "fa fa-copy"},
          {class: "btn-danger btn-delete", icon: "fa fa-trash-o"}
        ]
      };
    });
    setTimeout(() => this.ref.markForCheck(), 50);
  }

  async loadWorkspaces(forced = false) {
    if (this.loadingWorkspaceSession) {
      await this.loadingWorkspaceSession;
    }
    this.loadingWorkspaceSession = this.pbiservice.getWorkspaceList(forced);
    const response = await this.loadingWorkspaceSession;
    return response.map(workspace => ({
      id: workspace.id,
      label: workspace.name,
      reports: !workspace.reports ? [] : workspace.reports.map(report => ({
        id: report.id,
        label: report.name
      }))
    }));
  }

  onActionClick([task, buttonIndex]: [TaskEntry, number]) {
    switch (buttonIndex) {
      case 0: return this.onChangeStatusClick(task);
      case 1: return this.onViewClick(task);
      case 2: return this.onEditClick(task);
      case 3: return this.onCloneClick(task);
      case 4: return this.onDeleteClick(task);
      default: throw new Error("Unexpected button");
    }
  }

  onAddClick() {
    this.router.navigate(['/tasks/new']);
  }

  onViewClick(task: TaskEntry) {
    this.router.navigate(['/tasks/view/', task.id]);
    console.log('View Click', task);
  }

  onEditClick(task: TaskEntry) {
    this.router.navigate(['/tasks/edit/', task.id]);
  }

  async onDeleteClick(task: TaskEntry) {
    try {
      const result = await this.modalService.open(this.removeModal).result;
      if (result !== 'Remove') {
        return;
      }
      await this.taskService.removeTask(task.id);
      this.shouldResetCache = true;
      this.modalService.dismissAll();
      this.loading = true;
      setTimeout(() => this.loadTaskAndLink(), 100);
    } catch(err) {
      if (this.error.active) {
        return;
      }
      this.error.active = true;
      if (err === undefined || err === null || err === '') {
        this.error.message = 'Error updating task status: Service rejected without error message';
      } else if (err && err.message) {
        this.error.message = 'Error updating task status: Service rejected with error: ' + err.message.toString();
      } else {
        this.error.message = 'Error updating task status: Service rejected with unknown object: ' + JSON.stringify(err);
      }
      this.error.stack = err && err.stack ? err.stack : null;
      this.loading = false;
    }
  }



  async onCloneClick(task: TaskEntry) {
    try {
      const result = await this.modalService.open(this.cloneModal).result;
      if (result !== 'Clone') {
        return;
      }
      this.shouldResetCache = true;
      this.loading = true;
      this.modalService.dismissAll();
      await this.taskService.cloneTask(task.id);
      setTimeout(() => this.loadTaskAndLink(), 100);
    } catch(err) {
      console.log(err)
      if (this.error.active ||err=='Close') {
        return;
      }
      this.error.active = true;
      if (err === undefined || err === null || err === '') {
        this.error.message = 'Error clonig task status: Service rejected without error message';
      } else if (err && err.message) {
        this.error.message = 'Error clonig task status: Service rejected with error: ' + err.message.toString();
      } else {
        this.error.message = 'Error clonig task status: Service rejected with unknown object: ' + JSON.stringify(err);
      }
      this.error.stack = err && err.stack ? err.stack : null;
      this.loading = false;
    }
  }

  setTaskStateVisually(task: TaskEntry, isActive: boolean) {
    task.actions[0].class = "btn-info btn-status" + (isActive ? "" : " deactivated");
    task.actions[0].icon = isActive ? "fa fa-check-circle" : "fa fa-times-circle";
    task.actions[0].label = isActive ? "Enabled" : "Disabled";
  }

  async onChangeStatusClick(task: TaskEntry) {
    this.currentTask = task;

    let result;
    try {
      const modalRef = this.modalService.open(this.changeStatusModal);
      result = await modalRef.result;
    } catch(err) {
      result = err;
    }
    try {
      this.modalService.dismissAll();
      if (result instanceof Error) {
        throw result;
      }
      if (result === 'enable') {
        this.setTaskStateVisually(task, true);
        await this.taskService.changeStatus(task.id, 'A');
        //this.loadTaskAndLink(false, false);
        if (this.currentTask) {
          this.currentTask.active = true;
        }
      } else if (result === "disable") {
        this.setTaskStateVisually(task, false);
        await this.taskService.changeStatus(task.id, 'I');
        if (this.currentTask) {
          this.currentTask.active = false;
        }
      }
    } catch(err) {
      if (this.error.active) {
        return;
      }
      this.error.active = true;
      if (err === undefined || err === null || err === '') {
        this.error.message = 'Error updating task status: Service rejected without error message';
      } else if (err && err.message) {
        this.error.message = 'Error updating task status: Service rejected with error: ' + err.message.toString();
      } else {
        this.error.message = 'Error updating task status: Service rejected with unknown object: ' + JSON.stringify(err);
      }
      this.error.stack = err && err.stack ? err.stack : null;
    }
  }

}
